import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Plus, X } from 'lucide-react';
import type { RankingItem, Participant } from '../types';

interface RankingViewProps {
  participant: Participant;
  items: RankingItem[];
  onRankingChange: (participantId: string, ranking: string[]) => void;
  onAddItem: () => void;
  onRemoveItem: (itemId: string) => void;
  canEdit: boolean;
}

interface SortableItemProps {
  item: RankingItem;
  rank: number;
  onRemove: () => void;
  canEdit: boolean;
}

function SortableItem({ item, rank, onRemove, canEdit }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: !canEdit });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-4 bg-[var(--bg-raised)] border  transition-all ${
        isDragging
          ? 'shadow-lg opacity-50 bg-[var(--slate-dim)] border-[var(--slate)]'
          : 'hover:shadow-md border-[var(--rule)]'
      }`}
    >
      {/* Rank Number */}
      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[var(--slate-dim)] text-[var(--text-sec)] flex items-center justify-center font-bold text-sm">
        {rank}
      </div>

      {/* Drag Handle */}
      {canEdit && (
        <button
          {...attributes}
          {...listeners}
          className="touch-none p-2 -m-2 text-[var(--text-ter)] hover:text-[var(--text-sec)] cursor-grab active:cursor-grabbing"
        >
          <GripVertical className="h-5 w-5" />
        </button>
      )}

      {/* Item Content */}
      <div className="flex-1 min-w-0">
        <div className="font-medium text-[var(--text-pri)]">{item.name}</div>
        {item.description && (
          <div className="text-sm text-[var(--text-ter)] mt-1">{item.description}</div>
        )}
      </div>

      {/* Remove Button */}
      {canEdit && (
        <button
          onClick={onRemove}
          className="text-[var(--text-ter)] hover:text-[var(--coral)] transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}

export function RankingView({
  participant,
  items,
  onRankingChange,
  onAddItem,
  onRemoveItem,
  canEdit,
}: RankingViewProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Get items in current ranking order
  const rankedItems = participant.ranking
    .map((id) => items.find((item) => item.id === id))
    .filter((item): item is RankingItem => item !== undefined);

  // Add any items not yet ranked
  const unrankedItems = items.filter(
    (item) => !participant.ranking.includes(item.id)
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = participant.ranking.indexOf(active.id as string);
    const newIndex = participant.ranking.indexOf(over.id as string);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newRanking = arrayMove(participant.ranking, oldIndex, newIndex);
      onRankingChange(participant.id, newRanking);
    } else if (oldIndex === -1 && newIndex !== -1) {
      // Adding unranked item
      const newRanking = [...participant.ranking];
      newRanking.splice(newIndex, 0, active.id as string);
      onRankingChange(participant.id, newRanking);
    } else if (oldIndex !== -1 && newIndex === -1) {
      // Moving to unranked (remove from ranking)
      const newRanking = participant.ranking.filter((id) => id !== active.id);
      onRankingChange(participant.id, newRanking);
    }
  };

  return (
    <div className="space-y-4">
      {/* Participant Header */}
      <div
        className="flex items-center gap-3 p-3"
        style={{ backgroundColor: `${participant.color}20` }}
      >
        <div
          className="w-4 h-4 rounded-full"
          style={{ backgroundColor: participant.color }}
        />
        <h3 className="font-semibold text-[var(--text-pri)]">{participant.name}</h3>
        <span className="text-sm text-[var(--text-ter)]">
          {rankedItems.length} of {items.length} ranked
        </span>
      </div>

      {/* Ranked Items */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={participant.ranking}
          strategy={verticalListSortingStrategy}
          disabled={!canEdit}
        >
          <div className="space-y-2">
            {rankedItems.map((item, index) => (
              <SortableItem
                key={item.id}
                item={item}
                rank={index + 1}
                onRemove={() => onRemoveItem(item.id)}
                canEdit={canEdit}
              />
            ))}
          </div>
        </SortableContext>

        {/* Unranked Items */}
        {unrankedItems.length > 0 && (
          <div className="mt-6 pt-6 border-t border-[var(--rule)]">
            <h4 className="text-sm font-medium text-[var(--text-ter)] mb-3">
              Unranked Items
            </h4>
            <SortableContext
              items={unrankedItems.map((item) => item.id)}
              strategy={verticalListSortingStrategy}
              disabled={!canEdit}
            >
              <div className="space-y-2">
                {unrankedItems.map((item) => (
                  <SortableItem
                    key={item.id}
                    item={item}
                    rank={0}
                    onRemove={() => onRemoveItem(item.id)}
                    canEdit={canEdit}
                  />
                ))}
              </div>
            </SortableContext>
          </div>
        )}
      </DndContext>

      {/* Add Item Button */}
      {canEdit && (
        <button
          onClick={onAddItem}
          className="w-full p-3 border-2 border-dashed border-[var(--rule)] text-[var(--text-ter)] hover:border-[var(--slate)] hover:text-[var(--text-ter)] transition-colors flex items-center justify-center gap-2"
        >
          <Plus className="h-5 w-5" />
          <span>Add Item</span>
        </button>
      )}
    </div>
  );
}

